import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Smartphone } from "lucide-react";
import { supabase } from "../supabaseClient";
import "../styles/components.css";

const mainLinks = [
    { to: "/", label: "Home" },
    { to: "/discover", label: "Discover" },
    { to: "/how-it-works", label: "How it Works", desktopOnly: true },
    { to: "/passes", label: "Passes", primary: true },
];

export default function NavBar() {
    const [user, setUser] = useState(null);
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [showAppPrompt, setShowAppPrompt] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const getSession = async () => {
            const { data } = await supabase.auth.getSession();
            setUser(data?.session?.user || null);
        };

        getSession();

        const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
            setUser(session?.user || null);
        });

        return () => listener.subscription.unsubscribe();
    }, []);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 16);
        handleScroll();

        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (typeof document === "undefined") return;

        document.body.classList.toggle("nav-menu-open", menuOpen);

        return () => document.body.classList.remove("nav-menu-open");
    }, [menuOpen]);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const ua = navigator.userAgent || "";
        const isMobileDevice = /android|iphone|ipad|ipod/i.test(ua);
        const isMobileWidth = window.matchMedia("(max-width: 900px)").matches;
        const dismissed = sessionStorage.getItem("bbp_app_prompt_dismissed") === "1";

        setShowAppPrompt(isMobileDevice && isMobileWidth && !dismissed);
    }, []);

    const handleLogout = async () => {
        const { error } = await supabase.auth.signOut({ scope: "local" });

        if (error) {
            console.warn("Logout error:", error.message);
        }

        setMenuOpen(false);
        navigate("/");
    };

    const dismissPrompt = () => {
        sessionStorage.setItem("bbp_app_prompt_dismissed", "1");
        setShowAppPrompt(false);
    };

    const isActivePath = (path) => {
        if (path === "/") return location.pathname === "/";
        return location.pathname.startsWith(path);
    };

    const navLinkClass = (link) => {
        const classes = ["nav-link"];

        if (link.primary) classes.push("nav-link--primary");
        if (link.desktopOnly) classes.push("nav-desktop-only");
        if (isActivePath(link.to)) classes.push("is-active");

        return classes.join(" ");
    };

    return (
        <>
            {showAppPrompt ? (
                <div className="app-prompt-banner" role="region" aria-label="Open in app prompt">
                    <div className="app-prompt-copy">
                        <span className="app-prompt-icon">
                            <Smartphone size={17} aria-hidden="true" />
                        </span>

                        <div>
                            <strong>App coming soon!</strong>

                        </div>
                    </div>

                    <div className="app-prompt-actions">
                        <button type="button" className="app-prompt-btn app-prompt-btn--ghost" onClick={dismissPrompt}>
                            Stay on site
                        </button>

                        <button type="button" className="app-prompt-btn" onClick={dismissPrompt}>
                            Sounds beachy
                        </button>
                    </div>
                </div>
            ) : null}

            <nav className={`navbar-classic ${scrolled ? "navbar--scrolled" : ""}`}>
                <div className="navbar-inner">
                    <button
                        type="button"
                        className="navbar-logo"
                        onClick={() => navigate("/")}
                        aria-label="Go to Beach Bum Pass home"
                    >
                        <img
                            src="/assets/Graphics/Logo.png"
                            alt="Beach Bum Pass Logo"
                            className="navbar-logo-img navbar-logo-img--large"
                        />

                        <span className="navbar-brand-text">
                            <span>Beach Bum</span>
                            <strong>Pass</strong>
                        </span>
                    </button>

                    <div className="navbar-center" aria-label="Primary navigation">
                        {mainLinks.map((link) => (
                            <Link key={link.to} to={link.to} className={navLinkClass(link)}>
                                {link.label}
                            </Link>
                        ))}
                    </div>

                    <div className="navbar-right">
                        <Link to="/vendor" className="nav-vendor-portal desktop-only">
                            Partner Portal
                        </Link>

                        {!user ? (
                            <div className="nav-auth-group nav-auth-group--desktop">
                                <Link to="/login" className="nav-link nav-link--subtle">
                                    Login
                                </Link>

                                <Link to="/register" className="nav-cta-pill">
                                    Get Started
                                </Link>
                            </div>
                        ) : (
                            <div className="nav-auth-group nav-auth-group--desktop">
                                <Link to="/account" className="nav-link nav-link--subtle">
                                    My Account
                                </Link>

                                <button
                                    type="button"
                                    className="nav-cta-pill nav-logout-btn"
                                    onClick={handleLogout}
                                >
                                    Logout
                                </button>
                            </div>
                        )}

                        <button
                            type="button"
                            className="nav-menu-toggle"
                            aria-label={menuOpen ? "Close menu" : "Open menu"}
                            aria-expanded={menuOpen}
                            aria-controls="mobile-nav-drawer"
                            onClick={() => setMenuOpen((open) => !open)}
                        >
                            {menuOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                </div>
            </nav>

            <div
                className={`nav-drawer-backdrop ${menuOpen ? "is-open" : ""}`}
                onClick={() => setMenuOpen(false)}
                aria-hidden="true"
            />

            <aside
                id="mobile-nav-drawer"
                className={`nav-drawer ${menuOpen ? "is-open" : ""}`}
                aria-label="Mobile navigation"
            >
                <div className="nav-drawer-header">
                    <div>
                        <p className="nav-drawer-eyebrow">Beach Bum Pass</p>
                        <h2>Explore the Coast</h2>
                    </div>

                    <button
                        type="button"
                        className="nav-drawer-close"
                        onClick={() => setMenuOpen(false)}
                        aria-label="Close menu"
                    >
                        <X size={18} />
                    </button>
                </div>

                <nav className="nav-drawer-links">
                    <Link to="/" className="nav-drawer-link">Home</Link>
                    <Link to="/discover" className="nav-drawer-link">Discover</Link>
                    <Link to="/passes" className="nav-drawer-link nav-drawer-link--primary">Passes</Link>
                    <Link to="/how-it-works" className="nav-drawer-link">How it Works</Link>
                    <Link to="/vendor" className="nav-drawer-link">Vendor Portal</Link>

                    {user ? (
                        <>
                            <Link to="/account" className="nav-drawer-link">My Account</Link>

                            <button
                                type="button"
                                className="nav-drawer-link nav-drawer-link--button"
                                onClick={handleLogout}
                            >
                                Logout
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="nav-drawer-link">Login</Link>
                            <Link to="/register" className="nav-drawer-link">Create Account</Link>
                        </>
                    )}
                </nav>

                <div className="nav-drawer-app-card">
                    <div>
                        <p className="nav-drawer-app-title">App coming soon!</p>
                        <p className="nav-drawer-app-copy">
                            We’re polishing the mobile app. For now, your pass, QR code, and checkout are ready right here on the website.
                        </p>
                    </div>

                    <button type="button" className="nav-drawer-app-btn" onClick={() => setMenuOpen(false)}>
                        Stay on Site
                    </button>
                </div>
            </aside>
        </>
    );
}